import { useRef } from 'react';
import { buildCost, slotBuildLayer, type StructureDef } from '@playnets/sim';
import { STRUCTURES } from '@playnets/data';
import { useUi } from '../store';
import { StructureIcon } from './Icon';
import type { GameHandle } from '../game/useGame';

/**
 * The palette. Every gun this level offers, always on screen, always live.
 *
 * This replaces a modal. Building used to mean tapping a mount, waiting for a
 * dialog to slide up over a dimmed playfield, reading a grid, and tapping
 * again — four beats and a blackout for the one decision the game is actually
 * about. During a wave that was fatal: the interface was telling you not to
 * build at the exact moment building matters most, so people watched instead
 * of playing.
 *
 * Now a card is a thing you pick up. Drag it onto a mount and let go, or tap it
 * and then tap where it goes — both are the same two-part gesture and both work
 * mid-wave without covering anything. The playfield never dims and the sim
 * never slows.
 */
export default function BuildBar({ game }: { game: GameHandle | null }): JSX.Element | null {
  const hud = useUi((s) => s.hud);
  const sheet = useUi((s) => s.sheet);
  const armed = useUi((s) => s.armed);
  const paused = useUi((s) => s.paused);
  /** Where the pointer went down, so a tap can be told from a drag. */
  const from = useRef<{ x: number; y: number } | null>(null);
  const dragged = useRef(false);

  if (!game || paused || sheet !== null) return null;

  // Both layers in one row, cheapest first. Which mounts a card will actually
  // go on is answered on the board while you carry it, not by splitting the
  // bar in two — that would be a taxonomy lesson before the first decision.
  const offered = STRUCTURES.filter((s) => offeredHere(game, s)).sort(
    (a, b) => buildCost(a) - buildCost(b),
  );

  if (offered.length === 0) return null;

  const pick = (ev: React.PointerEvent, def: StructureDef): void => {
    ev.currentTarget.setPointerCapture(ev.pointerId);
    from.current = { x: ev.clientX, y: ev.clientY };
    dragged.current = false;
    useUi.getState().arm(def.id);
  };

  const carry = (ev: React.PointerEvent): void => {
    const start = from.current;
    if (!start || armed === null) return;
    if (!dragged.current && Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < 10) return;
    dragged.current = true;
    useUi.getState().setHoverSlot(game.dropTargetAt(ev.clientX, ev.clientY, armed));
  };

  const drop = (ev: React.PointerEvent): void => {
    const ui = useUi.getState();
    const id = ui.armed;
    from.current = null;
    if (id === null) return;
    // A tap leaves the card in hand so the next tap can place it. Only a drag
    // resolves here, against wherever it was let go.
    if (!dragged.current) return;
    const target = game.dropTargetAt(ev.clientX, ev.clientY, id);
    if (target !== null) game.place(target, id);
    ui.arm(null);
  };

  return (
    <div className="bar">
      {offered.map((def) => {
        const cost = buildCost(def);
        const poor = hud.juice < cost;
        const tint = `#${def.colour.toString(16).padStart(6, '0')}`;
        return (
          <button
            key={def.id}
            className={`card${armed === def.id ? ' is-armed' : ''}${poor ? ' is-poor' : ''}`}
            // Each card wears its own structure's colour, so recognising it in
            // the bar and recognising it on the board are the same act.
            style={{ ['--tint' as string]: tint }}
            onPointerDown={(e) => pick(e, def)}
            onPointerMove={carry}
            onPointerUp={drop}
            onPointerCancel={() => {
              from.current = null;
              useUi.getState().arm(null);
            }}
            aria-label={`${def.name}, ${cost} juice`}
          >
            <span className="card-glow" />
            <StructureIcon def={def} />
            <span className="card-cost">
              <i className="card-pip" />
              {cost}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Is this level offering the structure at all?
 *
 * Deliberately not `canBuild`, which also answers "right now, on that mount" —
 * occupancy and affordability both belong to the moment of dropping, not to
 * whether the card exists. A card you can't pay for yet still tells you what to
 * save toward, and a bar that changed shape as juice came in would be unusable.
 * It's dimmed instead.
 */
function offeredHere(game: GameHandle, def: StructureDef): boolean {
  const w = game.world;
  if (def.mergeOnly) return false;
  if (!w.unlocked.has(def.id)) return false;
  if (w.level.structures && !w.level.structures.includes(def.id)) return false;
  if (def.exclusiveTo && def.exclusiveTo !== w.planet.id) return false;
  // There has to be somewhere it could go — the opening level is all orbit, so
  // it has no business advertising crust hardware.
  return w.slots.some((s) => def.layer === 'both' || def.layer === slotBuildLayer(s));
}
